import { ADJECTIVES, NOUNS } from './words'

/**
 * A password a parent can read out and a child can remember: an adjective, a
 * noun and a three-digit number, like `sleepy-dragon-427`.
 *
 * Kids forget passwords constantly and the parent is the recovery path, so the
 * reset flow offers one of these instead of asking a parent to invent
 * something. A phrase rather than two random words because it reads aloud and
 * sticks better, and never doubles a word; two words rather than three because
 * a seven-year-old has to type it; three digits rather than two because the
 * extra keystroke is the cheapest three bits available. 150 words in each
 * list gives some twenty million combinations, about 24 bits — which matters
 * against online guessing, and then only if the API throttles logins; nothing
 * this short survives an offline attack on leaked hashes, and neither would
 * anything a parent types themselves. Thirteen or more characters of letters,
 * digits and dashes clears the platform's strength check by construction.
 * Every choice comes from the crypto source, never Math.random.
 */

/**
 * Three-digit numbers, minus the ones a classroom would snigger at: 420, 666
 * and anything containing 69. The words are screened the same way; a password
 * gets said aloud, and the number is part of it.
 */
export const NUMBERS: readonly number[] = Array.from({ length: 900 }, (_, i) => 100 + i).filter(
  (n) => n !== 420 && n !== 666 && !String(n).includes('69'),
)

export function suggestPassword(): string {
  const [first = 0, second = 0, third = 0] = crypto.getRandomValues(new Uint32Array(3))
  const adjective = ADJECTIVES[first % ADJECTIVES.length]
  const noun = NOUNS[second % NOUNS.length]
  const number = NUMBERS[third % NUMBERS.length]
  return `${adjective}-${noun}-${number}`
}
